import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getBranch, getAccessToken } from '../../store/user/selectors'

import { useThirdPartyApi } from '../../resources/hooks/api/thirdPartyApiHook'
import { useSettingsApi } from '../../resources/hooks/api/useSettingsApiHook'
import { URLS } from '../../resources/constants'
import toast from 'react-hot-toast'
import logoRrWhite from '../../assets/images/logo-rr-white.png'

interface Props { }

const QuickBookCallback: React.FC<Props> = () => {
    console.log('QuickBookCallback component loaded!')

    const { t } = useTranslation('scenes.thirdparty.connection');
    const { quickBookCallback } = useThirdPartyApi();
    const { fetchAccountSettingsData } = useSettingsApi();

    const branchData = useSelector(getBranch)
    const accessToken = useSelector(getAccessToken)
    const navigate = useNavigate()
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true)
    const branchId = +branchData['id']
    const hasHandledCallback = useRef(false)
    const _handleRetriveRecord = (code: string, state: string, realmId: string) => {
        setLoading(true)
        const params = {
            code: code,
            state: state,
            realmId: realmId,
            branch_id: branchId,
        }
        console.log('[DEBUG] Initiating quickBookCallback API request with params:', params);
        quickBookCallback(params, (message: string, resp: any) => {
            console.log('[DEBUG] quickBookCallback API call succeeded! Initiating fetchAccountSettingsData for branch:', branchId);
            fetchAccountSettingsData({ branch_id: branchId }, () => {
                const reconnectOrigin = localStorage.getItem('qbo_reconnect_origin');
                console.log('[DEBUG] fetchAccountSettingsData succeeded. reconnectOrigin:', reconnectOrigin);
                if (reconnectOrigin && reconnectOrigin.startsWith('dashboard')) {
                    console.log('[DEBUG] Navigating to Dashboard...');
                    navigate(URLS.DASHBOARD);
                } else {
                    console.log('[DEBUG] Navigating to Connection Wizard Dashboard...');
                    localStorage.removeItem('qbo_reconnect_origin');
                    navigate(URLS.QUICKBOOK_DASHBOARD);
                }
            }, (settingsMessage: string) => {
                console.log('[DEBUG] fetchAccountSettingsData API call failed with message:', settingsMessage);
                localStorage.removeItem('qbo_reconnect_origin');
                toast.error(settingsMessage)
                setLoading(false)
            })
        }, (message: string, resp: any) => {
            console.log('[DEBUG] quickBookCallback API call failed with message:', message, resp)
            localStorage.removeItem('qbo_reconnect_origin');
            toast.error(message)
            setLoading(false)
        })
    }

    useEffect(() => {
        // DEV ONLY: OAuth callback may arrive via an external URL (e.g. ngrok)
        // while auth tokens live in localStorage under the localhost origin.
        // REACT_APP_LOCAL_DEV_PORT is only set in docker-compose for local dev.
        console.log('[DEBUG] QuickBookCallback mount useEffect triggered. branchId:', branchId, 'accessToken:', !!accessToken);
        const localDevPort = process.env.REACT_APP_LOCAL_DEV_PORT;
        if (localDevPort && window.location.hostname !== 'localhost') {
            window.location.href = `http://localhost:${localDevPort}/quickbook-callback${window.location.search}`;
            return;
        }

        const code: string = searchParams.get('code') ? searchParams.get('code')! : '';
        const state: string = searchParams.get('state') ? searchParams.get('state')! : '';
        const realmId: string = searchParams.get('realmId') ? searchParams.get('realmId')! : '';

        if (!branchId || !accessToken) {
            console.log('[DEBUG] Missing branchId or accessToken, waiting...');
            return;
        }

        if (!code || !state || !realmId) {
            console.log('[DEBUG] Invalid callback params in URL:', { code: !!code, state: !!state, realmId: !!realmId });
            toast.error(t('callback.invalidResponse'))
            navigate(URLS.THIRD_PARTY_CONNECTION)
            return;
        }

        if (hasHandledCallback.current) {
            console.log('[DEBUG] Callback already handled, skipping...');
            return;
        }

        hasHandledCallback.current = true;
        console.log('[DEBUG] QBO code, state, realmId verified. Invoking _handleRetriveRecord...');
        _handleRetriveRecord(code, state, realmId);
    }, [accessToken, branchId, navigate, searchParams])

    // useEffect(() => {
    //     window.addEventListener('message', (event) => {
            /*dispatch(updateQuickBookSettings({
                user_account_settings: {
                    qbo_session_expired: false,
                    qbo_last_login: new Date().toISOString(),
                    third_party_steps_completed: 0
                }
            }));*/
            // window.close();
            // if (event.data === 'success') {
            //     console.log('quick book success message: ', event)
            // } else if (event.data === 'error') {
            //     console.log('quick book error message: ', event)
            // }
        // });

        // Cleanup event listener when component unmounts
    //     return () => {
    //         window.removeEventListener("message", (event) => {
    //             console.log('quick book message: ', event)
    //         });
    //     };
    // }, [])
    return (
        <div className="main-container flex-grow-1 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8fafc', minHeight: 'calc(100vh - 120px)' }}>
            <div className="card border-0 shadow-lg p-5 text-center" style={{ maxWidth: '500px', borderRadius: '24px', backgroundColor: '#ffffff' }}>
                <div className="d-flex align-items-center justify-content-center gap-4 mb-4">
                    <img src={logoRrWhite} alt="RevRex" style={{ height: '40px', filter: 'invert(1)' }} />
                    <div className="text-muted fw-light" style={{ fontSize: '1.5rem' }}>+</div>
                    <i className="fa fa-plug text-primary" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="h5 font-weight-700 text-dark mb-3">{t('callback.title')}</h3>
                <p className="text-muted mb-4 px-3" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                    {t('callback.description')}
                </p>
                <div className="d-flex align-items-center justify-content-center py-2">
                    <div className="spinner-border text-primary" style={{ width: '2.5rem', height: '2.5rem', borderWidth: '3px' }} role="status">
                        <span className="visually-hidden">{t('callback.loading')}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default QuickBookCallback
